import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { BYPASS_AUTH } from "./devBypass";
import { apiScope, msalInstance } from "./msalConfig";

const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const devBypassToken = import.meta.env.VITE_DEV_BYPASS_TOKEN ?? "";

class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

async function acquireToken(): Promise<string> {
  if (BYPASS_AUTH) {
    if (!devBypassToken) {
      console.warn("[api] BYPASS_AUTH ativo mas VITE_DEV_BYPASS_TOKEN vazio — API vai retornar 401");
    }
    return devBypassToken;
  }
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new ApiError(401, "Sem conta ativa — faça login");
  try {
    const result = await msalInstance.acquireTokenSilent({ account, scopes: [apiScope] });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: [apiScope] });
      throw new ApiError(401, "Redirecionando pra reautenticação");
    }
    throw e;
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {}
): Promise<T> {
  const token = await acquireToken();
  const { json, headers, ...rest } = init;
  const res = await fetch(`${apiUrl}${path}`, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : (rest.body as BodyInit | null | undefined),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, body?.detail ?? res.statusText, body);
  }
  return body as T;
}

export { ApiError };

/** GET binário autenticado → Blob (downloads de PDF/CSV, anexos). */
export async function apiFetchBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const token = await acquireToken();
  const res = await fetch(`${apiUrl}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text();
    let detail = res.statusText;
    try { detail = JSON.parse(text)?.detail ?? detail; } catch { /* corpo não-JSON */ }
    throw new ApiError(res.status, detail);
  }
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="?([^";]+)"?/.exec(cd);
  return { blob: await res.blob(), filename: m ? m[1] : null };
}

/** Baixa um arquivo autenticado e dispara o download no navegador. */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const { blob, filename } = await apiFetchBlob(path);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Upload multipart (não força Content-Type JSON). Reusa o mesmo bearer. */
export async function apiUploadFormData<T>(path: string, fd: FormData): Promise<T> {
  const token = await acquireToken();
  const res = await fetch(`${apiUrl}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body?.detail ?? res.statusText, body);
  return body as T;
}
